import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Save, Trash2 } from 'lucide-react'
import type { SplashAlert } from '@/types'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { TagExpressionInput } from '@/components/common/TagExpressionInput'
import { legacyTagsToExpression } from '@/lib/tagExpression'
import { TelegramDestinationSelect } from '@/components/settings/TelegramDestinationSelect'

interface SplashAlertEditSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  alert?: SplashAlert | null
  onSave: (alert: Partial<SplashAlert>) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}

export function SplashAlertEditSheet({
  open,
  onOpenChange,
  alert,
  onSave,
  onDelete,
}: SplashAlertEditSheetProps) {
  const isNew = !alert?.id
  const exchangeOptions = useExchangeOptions('futures_ticker')

  const [name, setName] = useState('')
  const [exchanges, setExchanges] = useState<string[]>([])
  const [lookbackMinutes, setLookbackMinutes] = useState([5])
  const [minChangePct, setMinChangePct] = useState([50])
  const [cooldownSeconds, setCooldownSeconds] = useState([300])
  const [enabled, setEnabled] = useState(true)
  const [enableTelegram, setEnableTelegram] = useState(true)
  const [enablePushover, setEnablePushover] = useState(false)
  const [telegramChatId, setTelegramChatId] = useState('')
  const [telegramReplyTo, setTelegramReplyTo] = useState('')
  const [tagExpression, setTagExpression] = useState('')
  const [legacyRequired, setLegacyRequired] = useState<string[]>([])
  const [legacyExcluded, setLegacyExcluded] = useState<string[]>([])

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (alert) {
      setName(alert.name || '')
      setExchanges(alert.exchanges || [])
      setLookbackMinutes([alert.filters?.splash_lookback_minutes || alert.splash_lookback_minutes || 5])
      setMinChangePct([alert.filters?.splash_min_change_pct || alert.splash_min_change_pct || 50])
      setCooldownSeconds([alert.cooldown_seconds || 300])
      setEnabled(alert.enabled !== false)
      setEnableTelegram(alert.enable_telegram !== false)
      setEnablePushover(alert.enable_pushover === true)
      setTelegramChatId(alert.telegram?.chat_id || '')
      setTelegramReplyTo(String(alert.telegram?.reply_to_message_id ?? ''))
      const existingExpression = alert.tag_expression || ''
      if (existingExpression) {
        setTagExpression(existingExpression)
        setLegacyRequired([])
        setLegacyExcluded([])
      } else {
        const required = alert.required_tags || []
        const excluded = alert.exclude_tags || []
        setLegacyRequired(required)
        setLegacyExcluded(excluded)
        if (required.length > 0 || excluded.length > 0) {
          setTagExpression(legacyTagsToExpression(required, [], excluded))
        } else {
          setTagExpression('')
        }
      }
    } else {
      setName('')
      setExchanges([])
      setLookbackMinutes([5])
      setMinChangePct([50])
      setCooldownSeconds([300])
      setEnabled(true)
      setEnableTelegram(true)
      setEnablePushover(false)
      setTelegramChatId('')
      setTelegramReplyTo('')
      setTagExpression('')
      setLegacyRequired([])
      setLegacyExcluded([])
    }
  }, [alert, open])

  const toggleExchange = (exchange: string) => {
    setExchanges((prev) =>
      prev.includes(exchange)
        ? prev.filter((e) => e !== exchange)
        : [...prev, exchange]
    )
  }

  const handleSave = async () => {
    if (!name.trim()) return

    setIsSaving(true)
    try {
      const alertData: Partial<SplashAlert> & { tag_expression?: string; alert_type?: string } = {
        ...(alert?.id && { id: alert.id }),
        name: name.trim(),
        alert_type: 'splash',
        exchanges,
        enabled,
        filters: {
          splash_lookback_minutes: lookbackMinutes[0],
          splash_min_change_pct: minChangePct[0],
        },
        cooldown_seconds: cooldownSeconds[0],
        enable_telegram: enableTelegram,
        enable_pushover: enablePushover,
        required_tags: [],
        exclude_tags: [],
        tag_expression: tagExpression,
      }

      if (enableTelegram && (telegramChatId || telegramReplyTo)) {
        alertData.telegram = {
          chat_id: telegramChatId,
          reply_to_message_id: telegramReplyTo || undefined,
        }
      }
      await onSave(alertData)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save alert:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!alert?.id || !onDelete) return
    if (!confirm('Are you sure you want to delete this alert?')) return

    setIsDeleting(true)
    try {
      await onDelete(alert.id)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to delete alert:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isNew ? 'Create Splash Alert' : 'Edit Splash Alert'}</SheetTitle>
          <SheetDescription>
            Detect sporadic price spikes — rapid changes in price over a short window.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="splash-alert-name">Alert Name</Label>
              <Input
                id="splash-alert-name"
                placeholder="Splash Alert"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="splash-alert-enabled">Enabled</Label>
              <Switch
                id="splash-alert-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Exchanges to Monitor</Label>
            <div className="flex flex-wrap gap-2">
              {exchangeOptions.map((exchange) => (
                <Badge
                  key={exchange}
                  variant={exchanges.includes(exchange) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleExchange(exchange)}
                >
                  {exchange}
                </Badge>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Lookback Window</Label>
              <span className="text-sm font-medium">{lookbackMinutes[0]} min</span>
            </div>
            <Slider
              value={lookbackMinutes}
              onValueChange={setLookbackMinutes}
              min={1}
              max={30}
              step={1}
            />
            <p className="text-xs text-muted-foreground">
              Compare current price against this many minutes ago
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Min Change Threshold</Label>
              <span className="text-sm font-medium">{minChangePct[0]}%</span>
            </div>
            <Slider
              value={minChangePct}
              onValueChange={setMinChangePct}
              min={5}
              max={200}
              step={5}
            />
            <p className="text-xs text-muted-foreground">
              Alert when price changes more than this percentage
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Cooldown</Label>
              <span className="text-sm font-medium">{cooldownSeconds[0]}s</span>
            </div>
            <Slider
              value={cooldownSeconds}
              onValueChange={setCooldownSeconds}
              min={30}
              max={1800}
              step={30}
            />
          </div>

          <Separator />

          {/* Notifications */}
          <div className="space-y-4">
            <Label className="text-base">Notifications</Label>

            <div className="flex items-center justify-between">
              <Label htmlFor="splash-enable-telegram">Telegram</Label>
              <Switch
                id="splash-enable-telegram"
                checked={enableTelegram}
                onCheckedChange={setEnableTelegram}
              />
            </div>

            {enableTelegram && (
              <div className="pl-4">
                <div className="space-y-2">
                  <Label htmlFor="splash-telegram-destination">Destination</Label>
                  <TelegramDestinationSelect
                    id="splash-telegram-destination"
                    chatId={telegramChatId}
                    replyToMessageId={telegramReplyTo}
                    onChange={({ chat_id, reply_to_message_id }) => {
                      setTelegramChatId(chat_id)
                      setTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="splash-enable-pushover">Pushover</Label>
              <Switch
                id="splash-enable-pushover"
                checked={enablePushover}
                onCheckedChange={setEnablePushover}
              />
            </div>
          </div>

          <Separator />

          {/* Tags */}
          <div className="space-y-4">
            <TagExpressionInput
              value={tagExpression}
              onChange={setTagExpression}
              legacyRequired={legacyRequired}
              legacyExcluded={legacyExcluded}
            />
          </div>
        </div>

        <SheetFooter className="gap-2 sm:gap-0">
          {!isNew && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || isSaving}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isDeleting || !name.trim()}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
